<template>
  <div>
    <h1>Alat Teller</h1>
    
    <!-- Navigasi Tab -->
    <div class="tabs">
      <button @click="activeTab = 'main'" :class="{ active: activeTab === 'main' }">Transaksi Baru</button>
      <button @click="activeTab = 'reprint'" :class="{ active: activeTab === 'reprint' }">Cetak Ulang Struk</button>
    </div>

    <!-- Konten Tab Transaksi -->
    <div v-show="activeTab === 'main'" class="teller-layout">
      <!-- Kolom 1: Setor Tunai -->
      <div class="form-container">
        <h2>Setor Tunai</h2>
        <div class="form-group">
          <label for="dest-account">Nomor Rekening Tujuan</label>
          <div class="verify-group">
            <input v-model="depositForm.destination_account_number" id="dest-account" @input="resetVerification('deposit')" />
            <button @click="verifyAccount('deposit')" :disabled="!depositForm.destination_account_number || isVerifying" class="btn btn-secondary">
              {{ isVerifying ? '...' : 'Verifikasi' }}
            </button>
          </div>
          <p v-if="depositVerification.error" class="error-message">{{ depositVerification.error }}</p>
          <p v-if="depositVerification.success" class="success-message">✓ {{ depositVerification.ownerName }}</p>
        </div>
        <div class="form-group">
          <label for="deposit-amount">Jumlah Setoran</label>
          <input v-model.number="depositForm.amount" id="deposit-amount" type="number" step="1000" min="10000" />
        </div>
        <div class="form-group">
          <label for="source-funds">Sumber Dana</label>
          <input v-model="depositForm.source_of_funds" id="source-funds" placeholder="Contoh: Gaji..." />
        </div>
        <button @click="handleDeposit" :disabled="isSubmitting || !depositVerification.success" class="btn btn-primary">
          {{ isSubmitting ? 'Memproses...' : 'Proses Deposit' }}
        </button>
      </div>

      <!-- Kolom 2: Tarik Tunai -->
      <div class="form-container">
        <h2>Tarik Tunai</h2>
        <div class="form-group">
          <label for="source-account">Nomor Rekening Sumber</label>
           <div class="verify-group">
            <input v-model="withdrawalForm.source_account_number" id="source-account" @input="resetVerification('withdrawal')" />
             <button @click="verifyAccount('withdrawal')" :disabled="!withdrawalForm.source_account_number || isVerifying" class="btn btn-secondary">
              {{ isVerifying ? '...' : 'Verifikasi' }}
            </button>
          </div>
          <p v-if="withdrawalVerification.error" class="error-message">{{ withdrawalVerification.error }}</p>
          <p v-if="withdrawalVerification.success" class="success-message">✓ {{ withdrawalVerification.ownerName }} (Saldo: {{ withdrawalVerification.balance }})</p>
        </div>
        <div class="form-group">
          <label for="withdrawal-amount">Jumlah Penarikan</label>
          <input v-model.number="withdrawalForm.amount" id="withdrawal-amount" type="number" step="1000" min="10000" />
        </div>
        <button @click="handleWithdrawal" :disabled="isSubmitting || !withdrawalVerification.success" class="btn btn-danger">
          {{ isSubmitting ? 'Memproses...' : 'Proses Tarik Tunai' }}
        </button>
      </div>
    </div>

    <!-- Konten Tab Cetak Ulang -->
    <div v-show="activeTab === 'reprint'" class="reprint-container">
      <h2>Cari dan Cetak Ulang Struk</h2>
      <div class="form-group">
        <label for="tx-id">Masukkan Nomor Referensi Transaksi</label>
        <div class="search-group">
          <input v-model="transactionIdToReprint" id="tx-id" type="number" placeholder="Contoh: 1, 2, 3..." />
          <button @click="downloadReceipt(transactionIdToReprint)" :disabled="!transactionIdToReprint" class="btn btn-primary">
            Cetak
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, h, onMounted, watch } from 'vue'; // Tambahkan watch
import api from '@/services/api';
import { useToast } from 'vue-toastification';

const activeTab = ref('main');
console.log("Nilai awal activeTab:", activeTab.value); // LOG 1

const transactionIdToReprint = ref(null);

const toast = useToast();
const isSubmitting = ref(false);
const isVerifying = ref(false);

const APPROVAL_LIMIT = 15000000;

const depositForm = reactive({ destination_account_number: '', amount: null, source_of_funds: '' });
const withdrawalForm = reactive({ source_account_number: '', amount: null });

const depositVerification = reactive({ success: false, error: null, ownerName: '' });
const withdrawalVerification = reactive({ success: false, error: null, ownerName: '', balance: '0' });

const formatCurrency = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toLocaleString('id-ID');
};

const resetVerification = (type) => {
  const target = type === 'deposit' ? depositVerification : withdrawalVerification;
  Object.assign(target, { success: false, error: null, ownerName: '', balance: '0' });
};

const verifyAccount = async (type) => {
  const targetForm = type === 'deposit' ? depositForm : withdrawalForm;
  const targetVerification = type === 'deposit' ? depositVerification : withdrawalVerification;
  const accountNumber = type === 'deposit' ? targetForm.destination_account_number : targetForm.source_account_number;

  if (!accountNumber) return;
  isVerifying.value = true;
  resetVerification(type);

  try {
    const response = await api.findAccountByNumber(accountNumber);
    targetVerification.ownerName = response.data.owner?.full_name || 'Nama Tidak Tersedia';
    targetVerification.balance = formatCurrency(response.data.balance);
    targetVerification.success = true;
  } catch (error) {
    targetVerification.error = error.response?.data?.detail || "Rekening tidak ditemukan.";
  } finally {
    isVerifying.value = false;
  }
};

const downloadReceipt = async (txId) => {
  if (!txId) {
    toast.error("Silakan masukkan nomor referensi.");
    return;
  }
  toast.info(`Mencari dan mengunduh struk untuk transaksi #${txId}...`);
  try {
    const response = await api.downloadReceipt(txId);
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    const fileName = `struk-transaksi-${txId}.pdf`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(err.response?.data?.detail || "Gagal mengunduh struk. Pastikan ID benar.");
  }
};

const ReceiptNotification = {
  props: ['txId', 'message'],
  setup(props) {
    return () => h('div', [
      h('p', props.message),
      h('button', {
        class: 'btn-toast-print',
        onClick: () => downloadReceipt(props.txId)
      }, 'Cetak Struk')
    ]);
  }
};

const handleDeposit = async () => {
  if (!depositForm.destination_account_number || !depositForm.amount || !depositForm.source_of_funds) {
    toast.error("Semua field harus diisi.");
    return;
  }
  isSubmitting.value = true;
  try {
    const response = await api.tellerDeposit(depositForm);
    
    if (depositForm.amount >= APPROVAL_LIMIT) {
      const { message, transaction_id } = response.data;
      toast.info(`${message} (ID: ${transaction_id})`);
    } else {
      // Hanya tampilkan tombol cetak jika transaksi langsung selesai
      toast.success({
        component: ReceiptNotification,
        props: {
          txId: response.data.last_transaction_id,
          message: `Deposit berhasil. Saldo baru: Rp ${formatCurrency(response.data.balance)}`
        }
      });
    }   
    Object.assign(depositForm, { destination_account_number: '', amount: null, source_of_funds: '' });
    resetVerification('deposit');
  } catch (error) {
    toast.error(`Gagal: ${error.response?.data?.detail || "Terjadi kesalahan."}`);
  } finally {
    isSubmitting.value = false;
  }
};

const handleWithdrawal = async () => {
  if (!withdrawalForm.source_account_number || !withdrawalForm.amount) {
    toast.error("Nomor rekening dan jumlah harus diisi.");
    return;
  }
  isSubmitting.value = true;
  try {
    const response = await api.tellerWithdrawal(withdrawalForm);

    if (withdrawalForm.amount >= APPROVAL_LIMIT) {
      toast.info("Transaksi besar berhasil diajukan & menunggu persetujuan Supervisor.");
    } else {
      // Hanya tampilkan tombol cetak jika transaksi langsung selesai
      toast.success({
        component: ReceiptNotification,
        props: {
          txId: response.data.last_transaction_id,
          message: `Tarik tunai berhasil. Saldo baru: Rp ${formatCurrency(response.data.balance)}`
        }
      });
    }
    Object.assign(withdrawalForm, { source_account_number: '', amount: null });
    resetVerification('withdrawal');
  } catch (error) {
    toast.error(`Gagal: ${error.response?.data?.detail || "Terjadi kesalahan."}`);
  } finally {
    isSubmitting.value = false;
  }
};


onMounted(() => {
    console.log("Komponen TellerPage dimuat, activeTab:", activeTab.value); // LOG 2
});

// Gunakan watch untuk melacak perubahan pada activeTab
watch(activeTab, (newValue, oldValue) => {
    console.log(`activeTab berubah dari '${oldValue}' menjadi '${newValue}'`); // LOG 3
});
</script>

<style scoped>
  .teller-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; margin-top: 2rem; }
  .form-container { padding: 1.5rem; border: 1px solid #ddd; border-radius: 8px; }
  .verify-group { display: flex; gap: 10px; }
  .verify-group input { flex-grow: 1; }
  .error-message { color: #dc3545; font-size: 0.9em; margin-top: 5px; }
  .success-message { color: #28a745; font-size: 0.9em; margin-top: 5px; }
  button:disabled { opacity: 0.6; cursor: not-allowed; }
  
  @media (max-width: 768px) {
    .teller-layout { grid-template-columns: 1fr; }
  }

  .btn-toast-print {
    margin-top: 10px;
    padding: 5px 10px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  .tabs { display: flex; border-bottom: 2px solid #ddd; margin-bottom: 2rem; }
  .tabs button { padding: 1rem 1.5rem; border: none; background: none; cursor: pointer; font-size: 1rem; position: relative; color: #555; }
  .tabs button.active { font-weight: bold; color: #007bff; }
  .tabs button.active::after { content: ''; position: absolute; bottom: -2px; left: 0; width: 100%; height: 2px; background-color: #007bff; }
  
  .reprint-container { max-width: 500px; }
  .search-group { display: flex; gap: 10px; }
</style>